import React, { forwardRef, Suspense, type JSX } from "react";

import { lazy } from "@loadable/component";
import Skeleton from "react-loading-skeleton";

import * as theme from "./theme.css";
import type { MarkdownEditorMethods, MarkdownEditorProps } from "./types";

// Lazy load the entire MDXEditor bundle
const InnerMarkdownEditor = lazy(
    () => import("components/markdownEditor/innerMarkdownEditor")
);

/**
 * Rich markdown editor (uncontrolled component) based on MDXEditor library.
 * It's  dynamically lazy loaded, because it has heavy dependencies.
 */
const MarkdownEditorWithoutPreload = forwardRef(
    (
        props: MarkdownEditorProps,
        ref: React.Ref<MarkdownEditorMethods>
    ): JSX.Element => {
        // NOTE: Using `...props` here is fine, because MarkdownEditorWithoutPreload and InnerMarkdownEditor have the same props.
        return (
            <Suspense
                fallback={
                    <Skeleton
                        className={theme.skeleton}
                        enableAnimation={true}
                    />
                }
            >
                <InnerMarkdownEditor ref={ref} {...props} />
            </Suspense>
        );
    }
);

// Add `preload` method which delegate to the inner lazy-loaded component.
// This way of adding `preload` prop also keeps TS happy.
const MarkdownEditor = Object.assign(MarkdownEditorWithoutPreload, {
    preload: InnerMarkdownEditor.preload,
});

export { MarkdownEditor };
export type { MarkdownEditorProps };
